import React, { useState } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import SuccessModal from './SuccessModal';
import './CreateUserForm.css';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Checkbox, 
  Typography, 
  Row, 
  Col, 
  Divider,
  InputNumber,
  DatePicker,
  message,
  Space
} from 'antd';
import { 
  EyeTwoTone, 
  EyeInvisibleOutlined, 
  KeyOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;

const initialFormData = {
  firstname: '',
  lastname: '',
  nickname: '',
  nationalid: '',
  date_of_birth: '',
  email: '',
  password: '',
  role: 'teacher',
  phone: '',
  address: '',
  bachelorDegree: '', 
  masterDegree: '', 
  doctoralDegree: '', 
  bachelorSchool: '', 
  masterSchool: '', 
  doctoralSchool: '', 
  specializeSubjects: '', 
  hoursRate: '', 
  schedule: '', 
  isPartTime: false,
  language: [],
};

function CreateTeacherForm({ onClose }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [formData, setFormData] = useState({
    ...initialFormData, 
    address: "The Planner Co., Ltd.", 
    phone: "0957262666",
  });
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPassword, setShowPassword] = useState(true);
  const [error, setError] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  const handleChange = (name, value) => {
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleLanguageChange = (lang) => {
    const selectedLanguages = formData.language.includes(lang)
      ? formData.language.filter(language => language !== lang)
      : [...formData.language, lang];
    
    setFormData(prevData => ({
      ...prevData,
      language: selectedLanguages
    }));
  };
  
  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    form.setFieldsValue({ password });
    setFormData(prevData => ({
      ...prevData,
      password: password
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prevShowPassword => !prevShowPassword);
  };
  
  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Check if date_of_birth is provided; if not, set to null
      const formattedDate = formData.date_of_birth 
        ? (typeof formData.date_of_birth === 'object' && formData.date_of_birth.format)
          ? formData.date_of_birth.format('YYYY-MM-DD')
          : new Date(formData.date_of_birth).toISOString().split('T')[0]
        : null;
  
      // Check if nationalid is provided; if not, set to null
      const nationalIdValue = formData.nationalid || null;
  
      const dataToSend = {
        ...formData,
        date_of_birth: formattedDate,
        nationalid: nationalIdValue,
        active: true,
        schedule: JSON.stringify(formData.schedule),
        language: formData.language,
        hoursRate: formData.hoursRate || 0,
      };
  
      const response = await axios.post(`${API_URL}/users/create`, dataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
  
      // Reset form and close modal if successful
      form.resetFields();
      setFormData(initialFormData);
      setShowConfirmModal(false);
      setError(null);
      message.success(t('form.teacher_created_successfully'));
      onClose();
  
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMessage = error.response ? error.response.data.message : 'Server error';
      setError(errorMessage);
      message.error(errorMessage);
    }
  };
  
  const onFinish = (values) => {
    // Update formData with form values
    const updatedData = {
      ...formData,
      ...values
    };
    setFormData(updatedData);
    setShowConfirmModal(true);
  };

  return (
    <div className="form-page">
      <Sidebar />
      <Header />
      <div className="form-content">
        <Card className="form-card">
          <Button 
            type="default" 
            icon={<ArrowLeftOutlined />} 
            onClick={onClose} 
            style={{ marginBottom: '16px' }}
          >
            {t('back')}
          </Button>
          
          <Title level={2} style={{ textAlign: 'center', marginBottom: '24px' }}>
            {t('form.add_new_teacher')}
          </Title>
          
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              ...formData,
              address: "The Planner Co., Ltd.", 
              phone: "0957262666",
            }}
          >
            <Divider orientation="left">
              <Text strong>{t('form.teacher_details')}</Text>
            </Divider>
            
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="firstname"
                  label={<span>{t('form.first_name')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_first_name') }]}
                >
                  <Input 
                    placeholder={t('form.first_name')} 
                    onChange={(e) => handleChange('firstname', e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="lastname"
                  label={<span>{t('form.last_name')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_last_name') }]}
                >
                  <Input 
                    placeholder={t('form.last_name')} 
                    onChange={(e) => handleChange('lastname', e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="nickname"
                  label={<span>{t('form.nickname')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_nickname') }]}
                >
                  <Input 
                    placeholder={t('form.nickname')} 
                    onChange={(e) => handleChange('nickname', e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="email"
                  label={<span>{t('form.email')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[
                    { required: true, message: t('form.please_enter_email') },
                    { type: 'email', message: t('form.please_enter_valid_email') }
                  ]}
                >
                  <Input 
                    placeholder={t('form.email')} 
                    onChange={(e) => handleChange('email', e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="phone"
                  label={<span>{t('form.phone')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_phone') }]}
                >
                  <Input 
                    placeholder={t('form.phone')} 
                    onChange={(e) => handleChange('phone', e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="password"
                  label={<span>{t('form.temporary_password')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_password') }]}
                >
                  <Input.Password
                    placeholder={t('form.temporary_password')}
                    onChange={(e) => handleChange('password', e.target.value)}
                    iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                    addonAfter={
                      <Button
                        type="text"
                        icon={<KeyOutlined />}
                        onClick={generateRandomPassword}
                        style={{ marginRight: -8 }}
                      />
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="address"
                  label={<span>{t('form.address')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_address') }]}
                >
                  <TextArea 
                    rows={4} 
                    placeholder={t('form.address')} 
                    onChange={(e) => handleChange('address', e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Divider orientation="left">
              <Text strong>{t('form.academic_details')}</Text>
            </Divider>
            
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="bachelorDegree"
                  label={<span>{t('form.bachelor_degree')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_bachelor_degree') }]}
                >
                  <Input 
                    placeholder={t('form.bachelor_degree')} 
                    onChange={(e) => handleChange('bachelorDegree', e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="bachelorSchool"
                  label={<span>{t('form.bachelor_school')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_bachelor_school') }]}
                >
                  <Input 
                    placeholder={t('form.bachelor_school')} 
                    onChange={(e) => handleChange('bachelorSchool', e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="masterDegree"
                  label={t('form.master_degree')}
                >
                  <Input 
                    placeholder={t('form.master_degree')} 
                    onChange={(e) => handleChange('masterDegree', e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="masterSchool"
                  label={t('form.master_school')}
                >
                  <Input 
                    placeholder={t('form.master_school')} 
                    onChange={(e) => handleChange('masterSchool', e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="doctoralDegree"
                  label={t('form.doctoral_degree')}
                >
                  <Input 
                    placeholder={t('form.doctoral_degree')} 
                    onChange={(e) => handleChange('doctoralDegree', e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="doctoralSchool"
                  label={t('form.doctoral_school')}
                >
                  <Input 
                    placeholder={t('form.doctoral_school')} 
                    onChange={(e) => handleChange('doctoralSchool', e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="specializeSubjects"
                  label={<span>{t('form.specialized_subjects')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_specialized_subjects') }]}
                >
                  <Input 
                    placeholder={t('form.specialized_subjects')} 
                    onChange={(e) => handleChange('specializeSubjects', e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="schedule"
                  label={<span>{t('form.about')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_about') }]}
                >
                  <Input 
                    placeholder={t('form.about')} 
                    onChange={(e) => handleChange('schedule', e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="isPartTime"
                  valuePropName="checked"
                >
                  <Checkbox 
                    onChange={(e) => handleChange('isPartTime', e.target.checked)}
                  >
                    {t('form.part_time')}
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={t('form.languages')}
                >
                  <Space>
                    <Checkbox 
                      checked={formData.language.includes('Thai')}
                      onChange={() => handleLanguageChange('Thai')}
                    >
                      Thai
                    </Checkbox>
                    <Checkbox 
                      checked={formData.language.includes('English')}
                      onChange={() => handleLanguageChange('English')}
                    >
                      English
                    </Checkbox>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item style={{ marginTop: "20px" }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                size="large" 
                block
              >
                {t('form.create_teacher')}
              </Button>
            </Form.Item>
          </Form>
          
          {error && (
            <div style={{ color: 'red', marginTop: '16px', textAlign: 'center' }}>
              {error}
            </div>
          )}
        </Card>
        
        {showConfirmModal && (
          <SuccessModal
            details={formData}
            onClose={() => setShowConfirmModal(false)}
            onConfirm={handleSubmit}
          />
        )}
      </div>
    </div>
  );
}

export default withRoleAccess(CreateTeacherForm, ['superadmin', 'admin']);